import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Slider,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Settings as SettingsIcon,
  ShoppingCart as ShoppingCartIcon,
  Star as StarIcon,
  Image as ImageIcon,
  Style as StyleIcon,
  Palette as PaletteIcon,
  RestartAlt as RestartAltIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getProductDetailSettings, saveProductDetailSettings } from '../../../services/ecommerceApi';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';

interface ProductDetailSettingsProps {
  companyId: string | null;
  configId: string;
}

// Ürün detay ayarları interface'i
interface ProductDetailConfig {
  enabled: boolean;
  layout: {
    imageGalleryPosition: 'left' | 'top';
    showImageThumbnails: boolean;
    showImageZoom: boolean;
    imageGalleryStyle: 'grid' | 'slider';
  };
  productInfo: {
    showSku: boolean;
    showBrand: boolean;
    showCategory: boolean;
    showStock: boolean;
    showRating: boolean;
    showDescription: boolean;
    showSpecifications: boolean;
    showReviews: boolean;
  };
  pricing: {
    showOriginalPrice: boolean;
    showDiscountPercentage: boolean;
    showTaxInfo: boolean;
    currencySymbol: string;
    priceFormat: 'before' | 'after';
  };
  actions: {
    showQuantitySelector: boolean;
    showAddToCart: boolean;
    showBuyNow: boolean;
    showWishlist: boolean;
    showShare: boolean;
  };
  styling: {
    containerMaxWidth: number;
    imageHeight: number;
    buttonStyle: 'default' | 'rounded' | 'square';
    colorScheme: 'light' | 'dark' | 'auto';
    addToCartButtonColor: string;
    buyNowButtonColor: string;
  };
  features: {
    enableBreadcrumb: boolean;
    enableRecommendations: boolean;
    enableRecentlyViewed: boolean;
    enableSocialProof: boolean;
  };
  services: {
    enableServices: boolean;
    freeShipping: {
      enabled: boolean;
      title: string;
      description: string;
      icon: string;
    };
    returnPolicy: {
      enabled: boolean;
      title: string;
      description: string;
      icon: string;
    };
    securePayment: {
      enabled: boolean;
      title: string;
      description: string;
      icon: string;
    };
  };
}

// Varsayılan ayarlar
const defaultConfig: ProductDetailConfig = {
  enabled: true,
  layout: {
    imageGalleryPosition: 'left',
    showImageThumbnails: true,
    showImageZoom: true,
    imageGalleryStyle: 'slider',
  },
  productInfo: {
    showSku: true,
    showBrand: true,
    showCategory: false,
    showStock: true,
    showRating: true,
    showDescription: true,
    showSpecifications: true,
    showReviews: true,
  },
  pricing: {
    showOriginalPrice: true,
    showDiscountPercentage: true,
    showTaxInfo: true,
    currencySymbol: '₺',
    priceFormat: 'after',
  },
  actions: {
    showQuantitySelector: true,
    showAddToCart: true,
    showBuyNow: false,
    showWishlist: true,
    showShare: true,
  },
  styling: {
    containerMaxWidth: 1200,
    imageHeight: 500,
    buttonStyle: 'default',
    colorScheme: 'light',
    addToCartButtonColor: '#f97316', // Orange-500
    buyNowButtonColor: '#2563eb', // Blue-600
  },
  features: {
    enableBreadcrumb: true,
    enableRecommendations: true,
    enableRecentlyViewed: false,
    enableSocialProof: true,
  },
  services: {
    enableServices: true,
    freeShipping: {
      enabled: true,
      title: 'Ücretsiz Kargo',
      description: '150 TL ve üzeri alışverişlerde',
      icon: 'Truck'
    },
    returnPolicy: {
      enabled: true,
      title: '30 Gün İade',
      description: 'Koşulsuz iade garantisi',
      icon: 'RotateCcw'
    },
    securePayment: {
      enabled: true,
      title: 'Güvenli Ödeme',
      description: 'SSL sertifikalı güvenli ödeme',
      icon: 'Shield'
    }
  },
};

const normalizeServices = (raw: any): ProductDetailConfig['services'] => {
  const safeRaw = raw && typeof raw === 'object' ? raw : {};
  return {
    ...defaultConfig.services,
    ...safeRaw,
    freeShipping: {
      ...defaultConfig.services.freeShipping,
      ...(safeRaw.freeShipping && typeof safeRaw.freeShipping === 'object' ? safeRaw.freeShipping : {}),
    },
    returnPolicy: {
      ...defaultConfig.services.returnPolicy,
      ...(safeRaw.returnPolicy && typeof safeRaw.returnPolicy === 'object' ? safeRaw.returnPolicy : {}),
    },
    securePayment: {
      ...defaultConfig.services.securePayment,
      ...(safeRaw.securePayment && typeof safeRaw.securePayment === 'object' ? safeRaw.securePayment : {}),
    },
  };
};

// İkon seçenekleri (Lucide'da mevcut olan ikonlar)
const iconOptions = {
  shipping: [
    { value: 'Truck', label: '🚚 Truck (Kamyon)' },
    { value: 'Package', label: '📦 Package (Paket)' },
    { value: 'Zap', label: '⚡ Zap (Hızlı Kargo)' }
  ],
  return: [
    { value: 'RotateCcw', label: '🔄 RotateCcw (Döndür)' },
    { value: 'RefreshCw', label: '↻ RefreshCw (Yenile)' },
    { value: 'ArrowLeft', label: '← ArrowLeft (Sol Ok)' }
  ],
  payment: [
    { value: 'Shield', label: '🛡️ Shield (Güvenlik)' },
    { value: 'Lock', label: '🔒 Lock (Kilit)' },
    { value: 'CreditCard', label: '💳 CreditCard (Kredi Kartı)' },
    { value: 'CheckCircle', label: '✅ CheckCircle (Onaylandı)' }
  ]
};

const ProductDetailSettings: React.FC<ProductDetailSettingsProps> = ({ companyId, configId }) => {
  const { t } = useTranslation('salesSystem');
  const [config, setConfig] = useState<ProductDetailConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productDetailSettingsId, setProductDetailSettingsId] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isDetail2Active = Boolean((config.services as any)?.tacreoSettings?.enabled);

  // SQL'den ayarları yükle
  useEffect(() => {
    const loadProductDetailSettings = async () => {
      if (!configId || !companyId) { // ✅ companyId kontrolü eklendi
        console.log('⚠️ [PRODUCT DETAIL] ConfigId veya CompanyId yok, yükleme atlanıyor');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      console.log('🔄 [PRODUCT DETAIL] SQL den ayarlar yükleniyor...', { configId });

      try {
        const response = await getProductDetailSettings(companyId, configId); // ✅ companyId eklendi
        
        if (response.success && response.data) {
          console.log('✅ [PRODUCT DETAIL] Ayarlar başarıyla yüklendi:', response.data);
          setProductDetailSettingsId(response.data.id);
          
          // ServicesJson'u parse et
          let servicesData = defaultConfig.services;
          if (response.data.servicesJson) {
            try {
              servicesData = normalizeServices(JSON.parse(response.data.servicesJson));
            } catch (e) {
              console.error('❌ [PRODUCT DETAIL] ServicesJson parse hatası:', e);
              servicesData = normalizeServices(defaultConfig.services);
            }
          } else {
            servicesData = normalizeServices(defaultConfig.services);
          }
          
          // Backend'den gelen veriyi frontend formatına dönüştür
          setConfig({
            enabled: response.data.enabled ?? defaultConfig.enabled,
            layout: {
              imageGalleryPosition: response.data.imageGalleryPosition ?? defaultConfig.layout.imageGalleryPosition,
              showImageThumbnails: response.data.showImageThumbnails ?? defaultConfig.layout.showImageThumbnails,
              showImageZoom: response.data.showImageZoom ?? defaultConfig.layout.showImageZoom,
              imageGalleryStyle: response.data.imageGalleryStyle ?? defaultConfig.layout.imageGalleryStyle,
            },
            productInfo: {
              showSku: response.data.showSku ?? defaultConfig.productInfo.showSku,
              showBrand: response.data.showBrand ?? defaultConfig.productInfo.showBrand,
              showCategory: response.data.showCategory ?? defaultConfig.productInfo.showCategory,
              showStock: response.data.showStock ?? defaultConfig.productInfo.showStock,
              showRating: response.data.showRating ?? defaultConfig.productInfo.showRating,
              showDescription: response.data.showDescription ?? defaultConfig.productInfo.showDescription,
              showSpecifications: response.data.showSpecifications ?? defaultConfig.productInfo.showSpecifications,
              showReviews: response.data.showReviews ?? defaultConfig.productInfo.showReviews,
            },
            pricing: {
              showOriginalPrice: response.data.showOriginalPrice ?? defaultConfig.pricing.showOriginalPrice,
              showDiscountPercentage: response.data.showDiscountPercentage ?? defaultConfig.pricing.showDiscountPercentage,
              showTaxInfo: response.data.showTaxInfo ?? defaultConfig.pricing.showTaxInfo,
              currencySymbol: response.data.currencySymbol ?? defaultConfig.pricing.currencySymbol,
              priceFormat: response.data.priceFormat ?? defaultConfig.pricing.priceFormat,
            },
            actions: {
              showQuantitySelector: response.data.showQuantitySelector ?? defaultConfig.actions.showQuantitySelector,
              showAddToCart: response.data.showAddToCart ?? defaultConfig.actions.showAddToCart,
              showBuyNow: response.data.showBuyNow ?? defaultConfig.actions.showBuyNow,
              showWishlist: response.data.showWishlist ?? defaultConfig.actions.showWishlist,
              showShare: response.data.showShare ?? defaultConfig.actions.showShare,
            },
            styling: {
              containerMaxWidth: response.data.containerMaxWidth ?? defaultConfig.styling.containerMaxWidth,
              imageHeight: response.data.imageHeight ?? defaultConfig.styling.imageHeight,
              buttonStyle: response.data.buttonStyle ?? defaultConfig.styling.buttonStyle,
              colorScheme: response.data.colorScheme ?? defaultConfig.styling.colorScheme,
              addToCartButtonColor: response.data.addToCartButtonColor ?? defaultConfig.styling.addToCartButtonColor,
              buyNowButtonColor: response.data.buyNowButtonColor ?? defaultConfig.styling.buyNowButtonColor,
            },
            features: {
              enableBreadcrumb: response.data.enableBreadcrumb ?? defaultConfig.features.enableBreadcrumb,
              enableRecommendations: response.data.enableRecommendations ?? defaultConfig.features.enableRecommendations,
              enableRecentlyViewed: response.data.enableRecentlyViewed ?? defaultConfig.features.enableRecentlyViewed,
              enableSocialProof: response.data.enableSocialProof ?? defaultConfig.features.enableSocialProof,
            },
            services: normalizeServices(servicesData),
          });
        } else {
          console.log('ℹ️ [PRODUCT DETAIL] Ayar bulunamadı, varsayılan değerler kullanılıyor');
          setConfig(defaultConfig);
        }
      } catch (error: any) {
        console.error('❌ [PRODUCT DETAIL] Ayarlar yüklenirken hata:', error);
        setError('Ayarlar yüklenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen Hata'));
        setConfig(defaultConfig);
      } finally {
        setLoading(false);
      }
    };

    loadProductDetailSettings();
  }, [configId]);

  // Auto-save fonksiyonu (debounce ile)
  const autoSaveProductDetailSettings = async (updatedConfig: ProductDetailConfig) => {
    if (!companyId || !configId) {
      console.log('⚠️ [PRODUCT DETAIL AUTO-SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor');
      return;
    }

    // Önceki timer'ı iptal et
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // 1 saniye sonra kaydet
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        console.log('💾 [PRODUCT DETAIL AUTO-SAVE] Otomatik kayıt başlatılıyor...');
        
        // ServicesJson'u stringify et
        const servicesJson = JSON.stringify(updatedConfig.services);
        
        const data = {
          id: productDetailSettingsId,
          companyId: companyId,
          configId: configId,
          enabled: updatedConfig.enabled,
          // Layout
          imageGalleryPosition: updatedConfig.layout.imageGalleryPosition,
          showImageThumbnails: updatedConfig.layout.showImageThumbnails,
          showImageZoom: updatedConfig.layout.showImageZoom,
          imageGalleryStyle: updatedConfig.layout.imageGalleryStyle,
          // Product Info
          showSku: updatedConfig.productInfo.showSku,
          showBrand: updatedConfig.productInfo.showBrand,
          showCategory: updatedConfig.productInfo.showCategory,
          showStock: updatedConfig.productInfo.showStock,
          showRating: updatedConfig.productInfo.showRating,
          showDescription: updatedConfig.productInfo.showDescription,
          showSpecifications: updatedConfig.productInfo.showSpecifications,
          showReviews: updatedConfig.productInfo.showReviews,
          // Pricing
          showOriginalPrice: updatedConfig.pricing.showOriginalPrice,
          showDiscountPercentage: updatedConfig.pricing.showDiscountPercentage,
          showTaxInfo: updatedConfig.pricing.showTaxInfo,
          currencySymbol: updatedConfig.pricing.currencySymbol,
          priceFormat: updatedConfig.pricing.priceFormat,
          // Actions
          showQuantitySelector: updatedConfig.actions.showQuantitySelector,
          showAddToCart: updatedConfig.actions.showAddToCart,
          showBuyNow: updatedConfig.actions.showBuyNow,
          showWishlist: updatedConfig.actions.showWishlist,
          showShare: updatedConfig.actions.showShare,
          // Styling
          containerMaxWidth: updatedConfig.styling.containerMaxWidth,
          imageHeight: updatedConfig.styling.imageHeight,
          buttonStyle: updatedConfig.styling.buttonStyle,
          colorScheme: updatedConfig.styling.colorScheme,
          addToCartButtonColor: updatedConfig.styling.addToCartButtonColor,
          buyNowButtonColor: updatedConfig.styling.buyNowButtonColor,
          // Features
          enableBreadcrumb: updatedConfig.features.enableBreadcrumb,
          enableRecommendations: updatedConfig.features.enableRecommendations,
          enableRecentlyViewed: updatedConfig.features.enableRecentlyViewed,
          enableSocialProof: updatedConfig.features.enableSocialProof,
          // Services
          servicesJson: servicesJson,
        };
        
        const response = await saveProductDetailSettings(data);
        
        if (response.success) {
          console.log('✅ [PRODUCT DETAIL AUTO-SAVE] Otomatik kayıt başarılı');
          notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
          if (!productDetailSettingsId && response.data?.id) {
            setProductDetailSettingsId(response.data.id);
          }
        }
      } catch (error) {
        console.error('❌ [PRODUCT DETAIL AUTO-SAVE] Otomatik kayıt hatası:', error);
      }
    }, 1000);
  };

  // Ayar değişikliği handler'ı (auto-save ile)
  const handleConfigChange = (section: keyof ProductDetailConfig, key: string, value: any) => {
    const updatedSection = {
      ...(config[section] as any),
      [key]: value
    };
    const updatedConfig = {
      ...config,
      [section]: section === 'services' ? normalizeServices(updatedSection) : updatedSection
    };
    setConfig(updatedConfig);
    autoSaveProductDetailSettings(updatedConfig);
  };

  // Enabled ayarı için özel handler (auto-save ile)
  const handleEnabledChange = (value: boolean) => {
    const currentServices: any = config.services || {};
    const nextServices: any = { ...currentServices };

    // Ürün Detayları 1 açıldığında Ürün Detayları 2'yi otomatik kapat.
    if (value) {
      nextServices.tacreoSettings = {
        ...(currentServices.tacreoSettings || {}),
        enabled: false
      };
    }

    const updatedConfig = {
      ...config,
      enabled: value,
      services: nextServices
    };
    setConfig(updatedConfig);
    autoSaveProductDetailSettings(updatedConfig);
  };

  // Tüm ayarları sıfırla (auto-save ile)
  const resetToDefaults = () => {
    setConfig(defaultConfig);
    autoSaveProductDetailSettings(defaultConfig);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2" fontWeight="bold">
            {t('ecommerceProductDetail1.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={resetToDefaults}
            size="small"
          >
            {t('ecommerceProductDetail1.actions.resetToDefault')}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Genel Ayarlar */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ mr: 1 }} />
                {t('ecommerceProductDetail1.sections.generalSettings')}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enabled && !isDetail2Active}
                    onChange={(e) => handleEnabledChange(e.target.checked)}
                  />
                }
                label={t('ecommerceProductDetail1.fields.enableProductDetails1')}
              />
            </CardContent>
          </Card>
        </Grid>

                 {/* Layout Ayarları */}
         <Grid item xs={12} md={4}>
           <Card sx={{ mb: 2 }}>
             <CardContent>
               <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                 <StyleIcon sx={{ mr: 1 }} />
                 {t('ecommerceProductDetail1.sections.layoutSettings')}
               </Typography>
               
               <FormControl component="fieldset" sx={{ mb: 2 }}>
                 <FormLabel component="legend">{t('ecommerceProductDetail1.fields.imageGalleryPosition')}</FormLabel>
                 <RadioGroup
                   row
                   value={config.layout.imageGalleryPosition}
                   onChange={(e) => handleConfigChange('layout', 'imageGalleryPosition', e.target.value)}
                 >
                   <FormControlLabel value="left" control={<Radio />} label={t('ecommerceProductDetail1.options.left')} />
                   <FormControlLabel value="top" control={<Radio />} label={t('ecommerceProductDetail1.options.top')} />
                 </RadioGroup>
               </FormControl>

               <FormControl component="fieldset" sx={{ mb: 2 }}>
                 <FormLabel component="legend">{t('ecommerceProductDetail1.fields.imageGalleryStyle')}</FormLabel>
                 <RadioGroup
                   row
                   value={config.layout.imageGalleryStyle}
                   onChange={(e) => handleConfigChange('layout', 'imageGalleryStyle', e.target.value)}
                 >
                   <FormControlLabel value="slider" control={<Radio />} label={t('ecommerceProductDetail1.options.slider')} />
                   <FormControlLabel value="grid" control={<Radio />} label={t('ecommerceProductDetail1.options.grid')} />
                 </RadioGroup>
               </FormControl>

               <FormControlLabel
                 control={
                   <Switch
                     checked={config.layout.showImageThumbnails}
                     onChange={(e) => handleConfigChange('layout', 'showImageThumbnails', e.target.checked)}
                   />
                 }
                 label={t('ecommerceProductDetail1.fields.showThumbnails')}
               />
               <br />
               <FormControlLabel
                 control={
                   <Switch
                     checked={config.layout.showImageZoom}
                     onChange={(e) => handleConfigChange('layout', 'showImageZoom', e.target.checked)}
                   />
                 }
                 label={t('ecommerceProductDetail1.fields.enableImageZoom')}
               />
             </CardContent>
           </Card>

           {/* Eylem Butonları - Düzen Ayarları'nın hemen altına */}
           <Card sx={{ mb: 2 }}>
             <CardContent>
               <Typography variant="h6" gutterBottom>
                 {t('ecommerceProductDetail1.sections.actionButtons')}
               </Typography>
               
               <List dense>
                 {Object.entries(config.actions).map(([key, value]) => (
                   <ListItem key={key} sx={{ py: 0.5 }}>
                     <FormControlLabel
                       control={
                         <Switch
                           checked={value}
                           onChange={(e) => handleConfigChange('actions', key, e.target.checked)}
                           size="small"
                         />
                       }
                       label={getActionLabel(key, t)}
                     />
                   </ListItem>
                 ))}
               </List>
             </CardContent>
           </Card>

           {/* Stil Ayarları - Eylem Butonları'nın hemen altına */}
           <Card>
             <CardContent>
               <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                 <PaletteIcon sx={{ mr: 1 }} />
                 {t('ecommerceProductDetail1.sections.styleSettings')}
               </Typography>
               
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                 <Box>
                  <Typography variant="body2" gutterBottom>{t('ecommerceProductDetail1.fields.containerMaxWidth')}</Typography>
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Slider
                       value={config.styling.containerMaxWidth}
                       onChange={(e, value) => handleConfigChange('styling', 'containerMaxWidth', value)}
                       min={800}
                       max={1400}
                       step={50}
                       valueLabelDisplay="auto"
                       size="small"
                     />
                     <Typography variant="caption" sx={{ minWidth: '50px' }}>
                       {config.styling.containerMaxWidth}px
                     </Typography>
                   </Box>
                 </Box>

                 <Box>
                  <Typography variant="body2" gutterBottom>{t('ecommerceProductDetail1.fields.imageHeight')}</Typography>
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Slider
                       value={config.styling.imageHeight}
                       onChange={(e, value) => handleConfigChange('styling', 'imageHeight', value)}
                       min={300}
                       max={700}
                       step={50}
                       valueLabelDisplay="auto"
                       size="small"
                     />
                     <Typography variant="caption" sx={{ minWidth: '50px' }}>
                       {config.styling.imageHeight}px
                     </Typography>
                   </Box>
                 </Box>

                                 {/* 2 Sütunlu Buton Ayarları */}
                <Grid container spacing={2}>
                  {/* Sol: Buton Stili */}
                  <Grid item xs={6}>
                    <FormControl component="fieldset" size="small">
                      <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>{t('ecommerceProductDetail1.fields.buttonStyle')}</FormLabel>
                      <RadioGroup
                        value={config.styling.buttonStyle}
                        onChange={(e) => handleConfigChange('styling', 'buttonStyle', e.target.value)}
                      >
                        <FormControlLabel 
                          value="default" 
                          control={<Radio size="small" />} 
                          label={t('ecommerceProductDetail1.options.default')}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                        />
                        <FormControlLabel 
                          value="rounded" 
                          control={<Radio size="small" />} 
                          label={t('ecommerceProductDetail1.options.rounded')}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                        />
                        <FormControlLabel 
                          value="square" 
                          control={<Radio size="small" />} 
                          label={t('ecommerceProductDetail1.options.square')}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Sağ: Buton Renkleri */}
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1, display: 'block' }}>
                          {t('ecommerceProductDetail1.fields.addToCartButtonColor')}
                        </FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="color"
                            value={config.styling.addToCartButtonColor}
                            onChange={(e) => handleConfigChange('styling', 'addToCartButtonColor', e.target.value)}
                            style={{ 
                              width: '40px', 
                              height: '30px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          />
                          <TextField
                            size="small"
                            value={config.styling.addToCartButtonColor}
                            onChange={(e) => handleConfigChange('styling', 'addToCartButtonColor', e.target.value)}
                            sx={{ flexGrow: 1 }}
                            placeholder="#f97316"
                          />
                        </Box>
                      </Box>

                      <Box>
                        <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1, display: 'block' }}>
                          {t('ecommerceProductDetail1.fields.buyNowButtonColor')}
                        </FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="color"
                            value={config.styling.buyNowButtonColor}
                            onChange={(e) => handleConfigChange('styling', 'buyNowButtonColor', e.target.value)}
                            style={{ 
                              width: '40px', 
                              height: '30px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          />
                          <TextField
                            size="small"
                            value={config.styling.buyNowButtonColor}
                            onChange={(e) => handleConfigChange('styling', 'buyNowButtonColor', e.target.value)}
                            sx={{ flexGrow: 1 }}
                            placeholder="#2563eb"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
               </Box>
             </CardContent>
           </Card>
         </Grid>

        {/* Fiyatlandırma */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('ecommerceProductDetail1.sections.pricingSettings')}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">{t('ecommerceProductDetail1.fields.currency')}</FormLabel>
                    <RadioGroup
                      value={config.pricing.currencySymbol}
                      onChange={(e) => handleConfigChange('pricing', 'currencySymbol', e.target.value)}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <FormControlLabel value="TL" control={<Radio />} label={t('ecommerceProductDetail1.currencyOptions.tl')} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel value="₺" control={<Radio />} label={t('ecommerceProductDetail1.currencyOptions.turkishLiraSymbol')} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel value="$" control={<Radio />} label={t('ecommerceProductDetail1.currencyOptions.dollar')} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel value="€" control={<Radio />} label={t('ecommerceProductDetail1.currencyOptions.euro')} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel value="£" control={<Radio />} label={t('ecommerceProductDetail1.currencyOptions.sterling')} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel value="¥" control={<Radio />} label={t('ecommerceProductDetail1.currencyOptions.yen')} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel value="₽" control={<Radio />} label={t('ecommerceProductDetail1.currencyOptions.ruble')} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel value="₹" control={<Radio />} label={t('ecommerceProductDetail1.currencyOptions.rupee')} />
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label={t('ecommerceProductDetail1.fields.customCurrencySymbol')}
                    value={!['TL', '₺', '$', '€', '£', '¥', '₽', '₹'].includes(config.pricing.currencySymbol) ? config.pricing.currencySymbol : ''}
                    onChange={(e) => handleConfigChange('pricing', 'currencySymbol', e.target.value)}
                    size="small"
                    helperText={t('ecommerceProductDetail1.fields.customCurrencySymbolHelp')}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">{t('ecommerceProductDetail1.fields.currencyPosition')}</FormLabel>
                    <RadioGroup
                      row
                      value={config.pricing.priceFormat}
                      onChange={(e) => handleConfigChange('pricing', 'priceFormat', e.target.value)}
                    >
                      <FormControlLabel 
                        value="before" 
                        control={<Radio />} 
                        label={`${t('ecommerceProductDetail1.options.before')} (${config.pricing.currencySymbol} 100)`}
                      />
                      <FormControlLabel 
                        value="after" 
                        control={<Radio />} 
                        label={`${t('ecommerceProductDetail1.options.after')} (100 ${config.pricing.currencySymbol})`}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, mt: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      {t('ecommerceProductDetail1.pricePreview.title')}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('ecommerceProductDetail1.pricePreview.regularPrice')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {config.pricing.priceFormat === 'before' 
                            ? `${config.pricing.currencySymbol} 1.234,56`
                            : `1.234,56 ${config.pricing.currencySymbol}`
                          }
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('ecommerceProductDetail1.pricePreview.largePrice')}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          {config.pricing.priceFormat === 'before' 
                            ? `${config.pricing.currencySymbol} 2.999,99`
                            : `2.999,99 ${config.pricing.currencySymbol}`
                          }
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>{t('ecommerceProductDetail1.pricePreview.selectedCurrency')}</strong> {config.pricing.currencySymbol} |
                          <strong> {t('ecommerceProductDetail1.pricePreview.position')}</strong> {config.pricing.priceFormat === 'before' ? t('ecommerceProductDetail1.options.before') : t('ecommerceProductDetail1.options.after')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.pricing.showOriginalPrice}
                        onChange={(e) => handleConfigChange('pricing', 'showOriginalPrice', e.target.checked)}
                      />
                    }
                    label={t('ecommerceProductDetail1.fields.showOriginalPrice')}
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.pricing.showDiscountPercentage}
                        onChange={(e) => handleConfigChange('pricing', 'showDiscountPercentage', e.target.checked)}
                      />
                    }
                    label={t('ecommerceProductDetail1.fields.showDiscountPercentage')}
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.pricing.showTaxInfo}
                        onChange={(e) => handleConfigChange('pricing', 'showTaxInfo', e.target.checked)}
                      />
                    }
                    label={t('ecommerceProductDetail1.fields.showTaxInfo')}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

                 {/* Ürün Bilgileri */}
         <Grid item xs={12} md={4}>
           <Card sx={{ mb: 2 }}>
             <CardContent>
               <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                 <InfoIcon sx={{ mr: 1 }} />
                 {t('ecommerceProductDetail1.sections.productInformation')}
               </Typography>
               
               <List dense>
                 {Object.entries(config.productInfo).map(([key, value]) => (
                   <ListItem key={key} sx={{ py: 0.5 }}>
                     <FormControlLabel
                       control={
                         <Switch
                           checked={value}
                           onChange={(e) => handleConfigChange('productInfo', key, e.target.checked)}
                           size="small"
                         />
                       }
                       label={getProductInfoLabel(key, t)}
                     />
                   </ListItem>
                 ))}
               </List>
             </CardContent>
           </Card>

           {/* Ek Özellikler - Ürün Bilgileri'nin hemen altına */}
           <Card>
             <CardContent>
               <Typography variant="h6" gutterBottom>
                 {t('ecommerceProductDetail1.sections.additionalFeatures')}
               </Typography>
               
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                 {Object.entries(config.features).map(([key, value]) => (
                   <FormControlLabel
                     key={key}
                     control={
                       <Switch
                         checked={value}
                         onChange={(e) => handleConfigChange('features', key, e.target.checked)}
                         size="small"
                       />
                     }
                     label={getFeatureLabel(key, t)}
                     sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                   />
                 ))}
               </Box>
             </CardContent>
           </Card>
         </Grid>

        {/* Hizmet ve Garanti - Alt satırda 3 kolon */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ mr: 1 }} />
                {t('ecommerceProductDetail1.sections.serviceAndGuarantee')}
              </Typography>
              
              {/* Ana Switch */}
              <FormControlLabel
                control={
                  <Switch
                    checked={config.services.enableServices}
                    onChange={(e) => handleConfigChange('services', 'enableServices', e.target.checked)}
                  />
                }
                label={t('ecommerceProductDetail1.fields.showServicesSection')}
                sx={{ mb: 2, '& .MuiFormControlLabel-label': { fontWeight: 'bold' } }}
              />

              {config.services.enableServices && (
                <Grid container spacing={3}>
                  {/* Ücretsiz Kargo */}
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.services.freeShipping.enabled}
                              onChange={(e) => handleConfigChange('services', 'freeShipping', { ...config.services.freeShipping, enabled: e.target.checked })}
                              size="small"
                            />
                          }
                          label={t('ecommerceProductDetail1.services.freeShipping')}
                          sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'medium' } }}
                        />
                        {config.services.freeShipping.enabled && (
                          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField
                              size="small"
                              label={t('ecommerceProductDetail1.fields.title')}
                              value={config.services.freeShipping.title}
                              onChange={(e) => handleConfigChange('services', 'freeShipping', { ...config.services.freeShipping, title: e.target.value })}
                            />
                            <TextField
                              size="small"
                              label={t('ecommerceProductDetail1.fields.description')}
                              value={config.services.freeShipping.description}
                              onChange={(e) => handleConfigChange('services', 'freeShipping', { ...config.services.freeShipping, description: e.target.value })}
                            />
                            <FormControl fullWidth size="small">
                              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>{t('ecommerceProductDetail1.fields.icon')}</FormLabel>
                              <Select
                                value={config.services.freeShipping.icon}
                                onChange={(e) => handleConfigChange('services', 'freeShipping', { ...config.services.freeShipping, icon: e.target.value })}
                                size="small"
                              >
                                {iconOptions.shipping.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* İade Garantisi */}
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.services.returnPolicy.enabled}
                              onChange={(e) => handleConfigChange('services', 'returnPolicy', { ...config.services.returnPolicy, enabled: e.target.checked })}
                              size="small"
                            />
                          }
                          label={t('ecommerceProductDetail1.services.returnGuarantee')}
                          sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'medium' } }}
                        />
                        {config.services.returnPolicy.enabled && (
                          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField
                              size="small"
                              label={t('ecommerceProductDetail1.fields.title')}
                              value={config.services.returnPolicy.title}
                              onChange={(e) => handleConfigChange('services', 'returnPolicy', { ...config.services.returnPolicy, title: e.target.value })}
                            />
                            <TextField
                              size="small"
                              label={t('ecommerceProductDetail1.fields.description')}
                              value={config.services.returnPolicy.description}
                              onChange={(e) => handleConfigChange('services', 'returnPolicy', { ...config.services.returnPolicy, description: e.target.value })}
                            />
                            <FormControl fullWidth size="small">
                              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>{t('ecommerceProductDetail1.fields.icon')}</FormLabel>
                              <Select
                                value={config.services.returnPolicy.icon}
                                onChange={(e) => handleConfigChange('services', 'returnPolicy', { ...config.services.returnPolicy, icon: e.target.value })}
                                size="small"
                              >
                                {iconOptions.return.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Güvenli Ödeme */}
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.services.securePayment.enabled}
                              onChange={(e) => handleConfigChange('services', 'securePayment', { ...config.services.securePayment, enabled: e.target.checked })}
                              size="small"
                            />
                          }
                          label={t('ecommerceProductDetail1.services.securePayment')}
                          sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'medium' } }}
                        />
                        {config.services.securePayment.enabled && (
                          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField
                              size="small"
                              label={t('ecommerceProductDetail1.fields.title')}
                              value={config.services.securePayment.title}
                              onChange={(e) => handleConfigChange('services', 'securePayment', { ...config.services.securePayment, title: e.target.value })}
                            />
                            <TextField
                              size="small"
                              label={t('ecommerceProductDetail1.fields.description')}
                              value={config.services.securePayment.description}
                              onChange={(e) => handleConfigChange('services', 'securePayment', { ...config.services.securePayment, description: e.target.value })}
                            />
                            <FormControl fullWidth size="small">
                              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>{t('ecommerceProductDetail1.fields.icon')}</FormLabel>
                              <Select
                                value={config.services.securePayment.icon}
                                onChange={(e) => handleConfigChange('services', 'securePayment', { ...config.services.securePayment, icon: e.target.value })}
                                size="small"
                              >
                                {iconOptions.payment.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

// Yardımcı fonksiyonlar
const getProductInfoLabel = (key: string, t: any): string => {
  const labels: Record<string, string> = {
    showSku: t('ecommerceProductDetail1.productInfo.showSku'),
    showBrand: t('ecommerceProductDetail1.productInfo.showBrand'),
    showCategory: t('ecommerceProductDetail1.productInfo.showCategory'),
    showStock: t('ecommerceProductDetail1.productInfo.showStock'),
    showRating: t('ecommerceProductDetail1.productInfo.showRating'),
    showDescription: t('ecommerceProductDetail1.productInfo.showDescription'),
    showSpecifications: t('ecommerceProductDetail1.productInfo.showSpecifications'),
    showReviews: t('ecommerceProductDetail1.productInfo.showReviews'),
  };
  return labels[key] || key;
};

const getActionLabel = (key: string, t: any): string => {
  const labels: Record<string, string> = {
    showQuantitySelector: t('ecommerceProductDetail1.actionButtons.showQuantitySelector'),
    showAddToCart: t('ecommerceProductDetail1.actionButtons.showAddToCart'),
    showBuyNow: t('ecommerceProductDetail1.actionButtons.showBuyNow'),
    showWishlist: t('ecommerceProductDetail1.actionButtons.showWishlist'),
    showShare: t('ecommerceProductDetail1.actionButtons.showShare'),
  };
  return labels[key] || key;
};

const getFeatureLabel = (key: string, t: any): string => {
  const labels: Record<string, string> = {
    enableBreadcrumb: t('ecommerceProductDetail1.features.enableBreadcrumb'),
    enableRecommendations: t('ecommerceProductDetail1.features.enableRecommendations'),
    enableRecentlyViewed: t('ecommerceProductDetail1.features.enableRecentlyViewed'),
    enableSocialProof: t('ecommerceProductDetail1.features.enableSocialProof'),
  };
  return labels[key] || key;
};

export default ProductDetailSettings;
